import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { EASE } from "../../styles/surfaces";

// Matches the guard in components/Legal/LegalPage.jsx. Read once at module scope rather
// than per instance — a page can mount a couple of dozen of these.
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Fades and lifts its children into place the first time they scroll into view.
 *
 * Starts hidden only once the observer is actually attached: if IntersectionObserver is
 * missing, or the visitor has asked for reduced motion, the content is rendered plainly
 * and never animates. Nothing here can leave content permanently invisible.
 *
 * `index` staggers a row of siblings — pass the map index and each one starts 70ms after
 * the last, which reads as the row arriving together rather than four separate events.
 */
const Reveal = ({ children, index = 0, delay = 0, sx, ...rest }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") return undefined;

    const node = ref.current;
    if (!node) return undefined;

    // Anything already on screen at mount stays put — animating it would mean flashing
    // out content the visitor is looking at.
    if (node.getBoundingClientRect().top < window.innerHeight) return undefined;

    setShown(false);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect(); // fires once
        }
      },
      // Fire slightly before the element reaches the fold so it has finished settling by
      // the time it is properly in view.
      { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(14px)",
        transition: `opacity .55s ${EASE} ${index * 70 + delay}ms, transform .55s ${EASE} ${
          index * 70 + delay
        }ms`,
        "@media (prefers-reduced-motion: reduce)": {
          opacity: 1,
          transform: "none",
          transition: "none",
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Reveal;
